import React, { useState, useEffect, useCallback } from 'react';
import {
  FiSearch,
  FiUsers,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiEye,
  FiEdit2,
  FiTrash2,
  FiMoreVertical,
  FiUserCheck,
  FiUserX
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useNotification } from '../../contexts/NotificationContext';
import { adminAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const ParentsManagement = () => {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedParent, setSelectedParent] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, suspended: 0 });
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();

  const fetchParents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers({ role: 'parent' });
      const raw = response?.data?.data || [];
      const normalized = raw.map((u) => ({
        id: u._id || u.id,
        _id: u._id || u.id,
        name: u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim(),
        email: u.email || '',
        phone: u.phone || '',
        status: u.status || (u.isActive === false ? 'inactive' : 'active'),
        children: u.children || [],
        createdAt: u.createdAt,
        updatedAt: u.updatedAt
      }));
      setParents(normalized);
    } catch (error) {
      showError('Failed to fetch parents');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminAPI.getUsersStats({ role: 'parent' });
      const data = response?.data?.data || {};
      setStats({ total: data.total || 0, active: data.active || 0, inactive: data.inactive || 0, suspended: data.suspended || 0 });
    } catch (_) {}
  }, []);

  useEffect(() => {
    fetchParents();
    fetchStats();
  }, [fetchParents, fetchStats]);

  const handleStatusChange = async (parentId, newStatus) => {
    try {
      await adminAPI.updateUserStatus(parentId, newStatus);
      showSuccess('Parent status updated');
      fetchParents();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to update parent status');
    }
  };

  const handleDeleteParent = async (parentId) => {
    if (!window.confirm('Are you sure you want to delete this parent? This action cannot be undone.')) return;
    try {
      await adminAPI.deleteUser(parentId);
      showSuccess('Parent deleted');
      fetchParents();
      fetchStats();
      setMenuOpen(null);
    } catch (error) {
      showError('Failed to delete parent');
    }
  };

  const handleViewParent = (parent) => {
    setSelectedParent(parent);
    setViewDialogOpen(true);
  };

  const filteredParents = parents.filter((p) => {
    const matchSearch = (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.email || '').toLowerCase().includes(searchTerm.toLowerCase()) || (p.phone || '').includes(searchTerm);
    const matchStatus = statusFilter === 'all' || p.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const statusBadge = (status) => {
    const map = { active: 'bg-emerald-100 text-emerald-800', inactive: 'bg-slate-100 text-slate-700', suspended: 'bg-red-100 text-red-800' };
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>;
  };

  if (loading && parents.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Parents</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage parent accounts and linked children.</p>
          </div>
          <button type="button" onClick={() => navigate('/admin/users/new?role=parent')} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
            Add New Parent
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-6 py-3 px-4 rounded-lg bg-white border border-slate-200/80 shadow-sm mb-6">
          <div className="flex items-center gap-2">
            <FiUsers className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Total</span>
            <span className="text-sm font-semibold text-slate-900">{stats.total}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiCheckCircle className="h-4 w-4 text-emerald-500" />
            <span className="text-sm text-slate-600">Active</span>
            <span className="text-sm font-semibold text-slate-900">{stats.active}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiXCircle className="h-4 w-4 text-slate-400" />
            <span className="text-sm text-slate-600">Inactive</span>
            <span className="text-sm font-semibold text-slate-900">{stats.inactive}</span>
          </div>
          <div className="h-4 w-px bg-slate-200" />
          <div className="flex items-center gap-2">
            <FiAlertCircle className="h-4 w-4 text-red-500" />
            <span className="text-sm text-slate-600">Suspended</span>
            <span className="text-sm font-semibold text-slate-900">{stats.suspended}</span>
          </div>
        </div>

        <div className="mb-6 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-sm">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name, email, or phone..." className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-400" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          {filteredParents.length > 0 && <span className="text-xs text-slate-500 sm:ml-auto">{filteredParents.length} of {parents.length}</span>}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : filteredParents.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiUsers className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No parents match your criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/80">
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Parent</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Contact</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Children</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-slate-700">Joined</th>
                    <th className="px-4 py-3 w-0" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredParents.map((parent) => (
                    <tr key={parent.id} className="hover:bg-slate-50/50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 shrink-0">
                            {parent.name && parent.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{parent.name}</div>
                            <div className="text-xs text-slate-500">ID: {parent.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-0.5">
                          <div className="text-slate-600">{parent.email}</div>
                          {parent.phone && <div className="text-xs text-slate-500">{parent.phone}</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{parent.children?.length || 0} children</td>
                      <td className="px-4 py-3">{statusBadge(parent.status)}</td>
                      <td className="px-4 py-3 text-slate-600">{parent.createdAt ? new Date(parent.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end">
                          <button type="button" onClick={() => handleViewParent(parent)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="View"><FiEye className="h-4 w-4" /></button>
                          <button type="button" onClick={() => navigate(`/admin/users/${parent.id}/edit`)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors" title="Edit"><FiEdit2 className="h-4 w-4" /></button>
                          <div className="relative inline-block">
                            <button type="button" onClick={() => setMenuOpen(menuOpen === parent.id ? null : parent.id)} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"><FiMoreVertical className="h-4 w-4" /></button>
                            {menuOpen === parent.id && (
                              <div className="absolute right-0 top-full mt-1 py-1 w-40 rounded-lg border border-slate-200 bg-white shadow-lg z-10">
                                <button type="button" onClick={() => handleStatusChange(parent._id, parent.status === 'active' ? 'inactive' : 'active')} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">{parent.status === 'active' ? <FiUserX className="h-4 w-4" /> : <FiUserCheck className="h-4 w-4" />}{parent.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                                <button type="button" onClick={() => handleStatusChange(parent._id, 'suspended')} disabled={parent.status === 'suspended'} className="w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50">Suspend</button>
                                <button type="button" onClick={() => handleDeleteParent(parent._id)} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"><FiTrash2 className="h-4 w-4" /> Delete</button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {viewDialogOpen && selectedParent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setViewDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Parent Details</h3>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-slate-500 mb-0.5">Name</div>
                <div className="font-medium text-slate-900">{selectedParent.name}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Email</div>
                <div className="text-slate-900">{selectedParent.email}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Phone</div>
                <div className="text-slate-900">{selectedParent.phone || 'Not provided'}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Status</div>
                <div>{statusBadge(selectedParent.status)}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">User ID</div>
                <div className="text-slate-900 font-mono text-xs">{selectedParent.id}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Joined</div>
                <div className="text-slate-900">{new Date(selectedParent.createdAt).toLocaleDateString()}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Last updated</div>
                <div className="text-slate-900">{selectedParent.updatedAt ? new Date(selectedParent.updatedAt).toLocaleDateString() : '—'}</div>
              </div>
              <div>
                <div className="text-slate-500 mb-0.5">Children</div>
                <div className="text-slate-900">{selectedParent.children?.length || 0}</div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setViewDialogOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 border border-slate-200 rounded-lg bg-white hover:bg-slate-50">Close</button>
              <button type="button" onClick={() => { setViewDialogOpen(false); navigate(`/admin/users/${selectedParent.id}/edit`); }} className="px-4 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800">Edit Parent</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ParentsManagement;
